import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Canvas } from "@react-three/fiber";
import { Environment, Sky } from "@react-three/drei";
import { motion } from "framer-motion";
import { ArrowLeft, Trophy, Pause, Play, Volume2, VolumeX, Coins } from "lucide-react";
import { usePlayer } from "@/context/PlayerContext";
import { heartbeat } from "@/lib/api";
import { formatDuration, formatDistance } from "@/lib/time";
import CarScene from "@/components/game/CarScene";
import { Button } from "@/components/ui/button";
import useEngineSound from "@/hooks/useEngineSound";

export default function Game() {
  const navigate = useNavigate();
  const { player, updatePlayer, loading } = usePlayer();
  const [paused, setPaused] = useState(false);
  const [hud, setHud] = useState({ speed: 0, distance: 0, sessionSeconds: 0, score: 0, coins: 0 });
  const distanceRef = useRef(0);
  const scoreRef = useRef(0);
  const secondsRef = useRef(0);
  const coinsRef = useRef(0);
  const sound = useEngineSound();

  useEffect(() => {
    if (loading) return;
    if (!player) navigate("/");
  }, [player, loading, navigate]);

  // Session ticker + heartbeat every 5s
  useEffect(() => {
    if (!player || paused) return;
    let sinceHb = 0;
    let hbDistance = 0;
    let hbCoinsSent = 0;
    const interval = setInterval(() => {
      secondsRef.current += 1;
      sinceHb += 1;
      setHud(h => ({ ...h, sessionSeconds: secondsRef.current }));

      if (sinceHb >= 5) {
        const distanceDelta = Math.max(0, distanceRef.current - hbDistance);
        hbDistance = distanceRef.current;
        const coinsDelta = Math.max(0, coinsRef.current - hbCoinsSent);
        hbCoinsSent = coinsRef.current;
        heartbeat({
          player_id: player.id,
          seconds_delta: sinceHb,
          distance_delta: distanceDelta,
          current_score: scoreRef.current,
          coins_delta: coinsDelta,
        }).then(res => {
          updatePlayer({
            total_time_seconds: res.total_time_seconds,
            total_distance: res.total_distance,
            high_score: res.high_score,
            coins_collected: res.coins_collected,
          });
        }).catch(() => { /* ignore transient */ });
        sinceHb = 0;
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [player, paused, updatePlayer]);

  // Final heartbeat on unload
  useEffect(() => {
    if (!player) return;
    const handler = () => {
      const data = JSON.stringify({
        player_id: player.id,
        seconds_delta: 0,
        distance_delta: 0,
        current_score: scoreRef.current,
        coins_delta: 0,
      });
      try {
        navigator.sendBeacon(
          `${process.env.REACT_APP_BACKEND_URL}/api/sessions/heartbeat`,
          new Blob([data], { type: "application/json" })
        );
      } catch (_) { /* noop */ }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [player]);

  const onTelemetry = (t) => {
    distanceRef.current = t.distance;
    scoreRef.current = Math.floor(t.distance) + coinsRef.current * 100 + Math.floor(t.maxSpeed * 3);
    setHud({
      speed: t.speed,
      distance: t.distance,
      score: scoreRef.current,
      coins: coinsRef.current,
      sessionSeconds: secondsRef.current,
    });
    sound.setSpeed(t.speed);
  };

  const onCoinCollected = () => {
    coinsRef.current += 1;
  };

  if (!player) return null;
  const carColor = player.car_color || "#06B6D4";

  return (
    <div className="fixed inset-0 w-full h-full bg-[#050505] overflow-hidden" data-testid="game-page">
      <div className="absolute inset-0 z-0" data-testid="game-canvas-wrapper">
        <Canvas
          shadows
          camera={{ position: [0, 5, 10], fov: 55 }}
          gl={{ antialias: true, powerPreference: "high-performance" }}
        >
          <color attach="background" args={["#0b0510"]} />
          <fog attach="fog" args={["#0b0510", 40, 220]} />
          <ambientLight intensity={0.35} />
          <directionalLight position={[10, 20, 5]} intensity={1.1} castShadow color="#ff9a56" />
          <directionalLight position={[-8, 6, -6]} intensity={0.5} color="#06b6d4" />
          <Sky sunPosition={[100, 8, -100]} turbidity={8} rayleigh={4} inclination={0.49} azimuth={0.25} />
          <Environment preset="sunset" background={false} />
          <CarScene onTelemetry={onTelemetry} onCoinCollected={onCoinCollected} paused={paused} carColor={carColor} />
        </Canvas>
      </div>

      <div className="absolute inset-0 z-30 pointer-events-none p-6 md:p-10 flex flex-col justify-between" data-testid="hud-overlay">
        {/* Top bar */}
        <div className="flex justify-between items-start gap-4">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
            className="bg-black/60 backdrop-blur-xl border border-white/10 px-5 py-3 pointer-events-auto scanlines relative">
            <Link to="/" className="font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-cyan-400 inline-flex items-center gap-2 transition-colors" data-testid="exit-game-link">
              <ArrowLeft className="w-3 h-3" /> Exit garage
            </Link>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="bg-black/60 backdrop-blur-xl border border-white/10 px-6 py-4 pointer-events-auto scanlines relative min-w-[260px]" data-testid="hud-session">
            <div className="flex items-center justify-between">
              <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Session Time</div>
              <button onClick={sound.toggle} data-testid="sound-toggle-button"
                className="text-zinc-400 hover:text-cyan-400 transition-colors" title={sound.enabled ? "Mute engine" : "Unmute engine"}>
                {sound.enabled ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
              </button>
            </div>
            <div className="font-mono text-2xl text-cyan-400 text-glow-cyan mt-1" data-testid="hud-session-time">
              {formatDuration(hud.sessionSeconds)}
            </div>
            <div className="mt-2 grid grid-cols-3 gap-3 font-mono text-[10px]">
              <div>
                <div className="text-zinc-500 uppercase tracking-widest">Total</div>
                <div className="text-white" data-testid="hud-total-time">{formatDuration((player.total_time_seconds || 0) + hud.sessionSeconds)}</div>
              </div>
              <div>
                <div className="text-zinc-500 uppercase tracking-widest">Best</div>
                <div className="text-yellow-400 text-glow-yellow" data-testid="hud-high-score">{Math.max(player.high_score || 0, hud.score)}</div>
              </div>
              <div>
                <div className="text-zinc-500 uppercase tracking-widest">Coins</div>
                <div className="text-yellow-400 flex items-center gap-1" data-testid="hud-coins">
                  <Coins className="w-3 h-3" /> {hud.coins}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom bar */}
        <div className="flex justify-between items-end gap-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="bg-black/60 backdrop-blur-xl border border-white/10 px-6 py-4 pointer-events-auto scanlines relative" data-testid="hud-speedometer">
            <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">Velocity</div>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="font-mono text-5xl md:text-6xl text-cyan-400 text-glow-cyan tabular-nums" data-testid="hud-speed-value">
                {Math.round(hud.speed)}
              </span>
              <span className="font-mono text-xs text-zinc-500 uppercase tracking-widest">km/h</span>
            </div>
            <div className="mt-3 h-1 w-56 bg-zinc-800 relative overflow-hidden">
              <div className="absolute inset-y-0 left-0 hud-track transition-[width] duration-200" style={{ width: `${Math.min(100, (hud.speed / 180) * 100)}%` }} />
            </div>
            <div className="mt-3 font-mono text-[10px] text-zinc-500 uppercase tracking-widest">
              Distance <span className="text-white ml-2" data-testid="hud-distance">{formatDistance(hud.distance)}</span>
            </div>
          </motion.div>

          <div className="flex flex-col items-end gap-3">
            <div className="bg-black/60 backdrop-blur-xl border border-white/10 px-5 py-3 pointer-events-auto scanlines relative">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Controls</div>
              <div className="mt-1 font-mono text-xs text-white flex gap-3 items-center">
                <Key label="W" /> <Key label="A" /> <Key label="S" /> <Key label="D" />
                <span className="text-zinc-500">drive · steer</span>
              </div>
              <div className="mt-2 font-mono text-[10px] text-zinc-500 uppercase tracking-widest inline-flex items-center gap-1">
                <Coins className="w-3 h-3 text-yellow-400" /> collect coins for <span className="text-yellow-400">+100</span> score
              </div>
            </div>
            <div className="flex gap-2 pointer-events-auto">
              <Button variant="outline" onClick={() => setPaused(p => !p)} data-testid="pause-button"
                className="rounded-none bg-black/60 border-white/10 hover:bg-cyan-500/10 hover:text-cyan-300 font-mono text-xs uppercase tracking-widest">
                {paused ? <><Play className="w-3 h-3 mr-2" /> Resume</> : <><Pause className="w-3 h-3 mr-2" /> Pause</>}
              </Button>
              <Button onClick={() => navigate("/leaderboard")} data-testid="hud-leaderboard-button"
                className="rounded-none bg-yellow-400 hover:bg-yellow-300 text-black font-mono text-xs uppercase tracking-widest">
                <Trophy className="w-3 h-3 mr-2" /> Leaderboard
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Sound prompt: shown once until user enables sound */}
      {!sound.enabled && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-40 pointer-events-auto" data-testid="sound-prompt">
          <button
            onClick={sound.enable}
            className="bg-black/70 backdrop-blur-xl border border-cyan-400/50 border-glow-cyan px-4 py-2 font-mono text-[11px] uppercase tracking-widest text-cyan-300 hover:text-white transition-colors flex items-center gap-2"
            data-testid="enable-sound-button">
            <Volume2 className="w-3 h-3" /> Turn on engine sound
          </button>
        </div>
      )}

      {paused && (
        <div className="absolute inset-0 z-40 bg-black/70 backdrop-blur-sm flex items-center justify-center" data-testid="pause-overlay">
          <div className="text-center">
            <div className="font-display font-black text-6xl uppercase tracking-tighter text-cyan-400 text-glow-cyan">Paused</div>
            <div className="font-mono text-xs text-zinc-400 mt-3 uppercase tracking-widest">Session timer continues</div>
            <Button className="mt-6 rounded-none bg-cyan-400 hover:bg-cyan-300 text-black font-mono text-xs uppercase tracking-widest" onClick={() => setPaused(false)} data-testid="resume-button">
              <Play className="w-3 h-3 mr-2" /> Resume
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Key({ label }) {
  return <span className="border border-white/20 bg-zinc-900/80 px-2 py-1 rounded-sm text-[10px] font-mono">{label}</span>;
}
